import { describeTilesetUse, framePlacementsOf } from "../document/tileset-change.ts";
import { holdsFrame } from "../document/tileset-frames.ts";
import type { TileRef, WorldDocument } from "../document/types.ts";
import type { ValidationIssue } from "./adapter.ts";

/**
 * The Issue for a pulled frame its pack no longer holds (ADR-129 decision 3.12). A pack's new version may lack a
 * frame a world pulled from the old one: the compile leaves it out of the editor's atlas and names it in the
 * editor's catalogue (`EditorCatalogue.lostPulls`), and each such frame the world's tileset list still holds is
 * one warning here. While nothing shows the frame the line offers Remove, which takes the pull out; while a prop
 * shows it the line names that placement instead, since the compile fails on it as on any missing frame. The
 * words name the frame by its tileset and tile, never by a frame id, which the Issues list would read as a name
 * the catalogue does not hold.
 */

/**
 * The kind of the Issue, which an ignore would bind to; the line is not ignorable.
 */
export const LOST_PULL_KIND = "pull.lost-frame";

/**
 * The lost pulls a world still lists, each as one warning of the map in view.
 *
 * @param world The document.
 * @param mapId The map the lines are listed with.
 * @param lost The frames the last compile found their pack lacks, as the editor's catalogue names them; anything
 *   but a list reads as none.
 * @returns One warning a frame that a partial entry of the world still holds, in the list's order.
 */
export function lostPullIssues(world: WorldDocument, mapId: string, lost: readonly TileRef[] | undefined): ValidationIssue[] {
  if (!Array.isArray(lost) || lost.length === 0) {
    return [];
  }
  const entries = new Map(world.tilesets.map((entry) => [entry.id, entry]));
  const held = (lost as readonly unknown[]).filter((frame): frame is TileRef => typeof frame === "object" && frame !== null && typeof (frame as TileRef).tileset === "string" && typeof (frame as TileRef).tile === "number").filter((frame) => {
    const entry = entries.get(frame.tileset);
    return entry?.frames !== undefined && holdsFrame(entry.frames, frame.tile);
  });
  if (held.length === 0) {
    return [];
  }
  const placed = framePlacementsOf(world, new Set(held.map((frame) => frame.tileset)));
  const withMap = world.maps.length > 1;
  return held.map((frame): ValidationIssue => {
    const what = `A frame pulled from ${frame.tileset}, tile ${frame.tile}, is not in its pack as the pack is now`;
    const use = placed.get(frame.tileset)?.get(frame.tile);
    const base = { mapId, kind: LOST_PULL_KIND, severity: "warning" as const, frame: { tileset: frame.tileset, tile: frame.tile } };
    return use === undefined
      ? { ...base, remedy: "remove-pull", message: `${what}, so the editor cannot show it; remove the pull` }
      : { ...base, message: `${what}, and ${describeTilesetUse(use, withMap)}, so the world will not compile; erase what shows it, then remove the pull` };
  });
}
